import { Breadcrumb, Button, Drawer, Empty, Flex, Form, Input, Popconfirm, Table, Tag, Tooltip, Typography, notification, theme } from 'antd';
import { HomeOutlined, DeleteOutlined, EyeOutlined, EditOutlined, LockOutlined, UnlockOutlined, FilterOutlined, WarningOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react'
import MasterDataAPI from '../../restAPI/MasterDataAPI';
import { useSelector } from 'react-redux';
import FilterComponent from '../../utils/FilterComponent';
import CommonSkeletonLoader from '../../helperComponents/CommonSkeleton';

const { Search } = Input;
const { Text, Link } = Typography;

export default function Department() {
    const { companyId, token, role } = useSelector(state => state.auth);
    const [form] = Form.useForm();
    const formValues = Form.useWatch([], form);
    const [departmentId, setDepartmentId] = useState("");
    const [initialDepartmentData, setInitialDepartmentData] = useState({ code: "", name: "" });
    const [actionState, setActionState] = useState('add');
    const [searchInputValue, setSearchInputValue] = useState("");
    const [filterValues, setFilterValues] = useState([]);
    const [departmentData, setDepartmentData] = useState([]);
    const [tempDepartmentData, setTempDepartmentData] = useState([]);
    const [open, setOpen] = useState(false);
    const [openFilter, setOpenFilter] = useState(false);
    const [showPopconfirm, setShowPopconfirm] = useState(false);
    const [cancelButtonDisabled, setCancelButtonDisabled] = useState(false);
    const [createButtonLoading, setCreateButtonLoading] = useState(false);
    const [isDepartmentListLoading, setIsDepartmentListLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [api, contextHolder] = notification.useNotification();
    const [btnPermission, setBtnPermission] = useState({
        "add": false,
        "edit": false,
        "delete": false,
        "view": false,
        "activeInactive": false
    });

    const openNotificationWithIcon = (type, message, description, placement) => {
        api[type]({
            message: message,
            description: description,
            placement
        });
    };



    const getDepartmentsData = async () => {
        try {
            const response = await MasterDataAPI.getDepartmentsDataByCompanyId(companyId, token);
            if (response.data.data) {
                setDepartmentData(response?.data?.data || [])
                setTempDepartmentData(response?.data?.data || [])
                setIsDepartmentListLoading(false);
            } else {
                setDepartmentData([])
                setTempDepartmentData([])
                setIsDepartmentListLoading(false);
            }
        } catch (error) {
            setDepartmentData([])
            setTempDepartmentData([])
            setIsDepartmentListLoading(false);
            console.log('get department data error :', error)
            openNotificationWithIcon('error', 'Error', `Get department data error`, 'bottomLeft');
        }
    }

    const showDrawer = () => {
        form.resetFields();
        setInitialDepartmentData({ code: "", name: "" });
        setActionState('add');
        setOpen(true);
    };
    const showFilterDrawer = () => {
        setOpenFilter(true);
    };

    const onClose = () => {
        form.resetFields();
        setInitialDepartmentData({ code: "", name: "" });
        setDepartmentId("");
        setOpen(false);
        setShowPopconfirm(false);
        setTimeout(() => {
            setActionState('add')
        }, 200)
    };
    const onCloseFilter = () => {
        setOpenFilter(false);
    };
    
    const getDepartmentCodeRules = useCallback(() => [
        {
            required: true,
            message: 'Department code is required',
        },
        {
            max: 10,
            message: 'Department code must be less than 10 characters',
        },
        {
            validator: (_, value) => {
                if (value && value.trim().length === 0) {
                    return Promise.reject(new Error('Department Code is not valid.'));
                }
                return Promise.resolve();
            },
        },
    ], []);

    const getDepartmentNameRules = useCallback(() => [
        {
            required: true,
            message: 'Department name is required',
        },
        {
            max: 50,
            message: 'Department name must be less than 50 characters',
        },
        {
            validator: (_, value) => {
                if (value && value.trim().length === 0) {
                    return Promise.reject(new Error('Department Name is not valid.'));
                }
                return Promise.resolve();
            },
        },
    ], []);

    const isFormModified = useCallback(() => {
        if (actionState !== 'edit') return true;
        
        if (!formValues) return false;
        
        const currentValues = form.getFieldsValue();
        const codeChanged = (currentValues.code || '').trim() !== (initialDepartmentData.code || '').trim();
        const nameChanged = (currentValues.name || '').trim() !== (initialDepartmentData.name || '').trim();
        
        return codeChanged || nameChanged;
    }, [actionState, formValues, initialDepartmentData, form]);

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();


    const columns = [
        {
            title: 'Department Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Department Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                record.status ? (<Tag color='purple' style={{ width: 90, textAlign: 'center' }}>Active</Tag>) : (<Tag color='warning' style={{ width: 90, textAlign: 'center' }}>Inactive</Tag>)
            ),
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Flex gap="small" justify="center" align="center" vertical>
                    <Flex gap="middle" wrap="wrap">
                        {/* edit icon */}
                        {btnPermission.edit &&
                            <Tooltip title="Edit Department" placement="top">
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    size='small'
                                    onClick={() => editHandler(record)}
                                    disabled={!record.status}
                                />
                            </Tooltip>
                        }
                        {/* lock icon */}
                        {
                            btnPermission.activeInactive && (
                                <Tooltip title="Active/Inactive Department" placement="top">
                                    <Popconfirm
                                        title={record.status ? "Do you want to inactivate the department?" : "Do you want to reactivate the department?"}
                                        onConfirm={() => editStatusHandler(record)}
                                        onCancel={() => openNotificationWithIcon('error', 'Abort', `Status update cancelled`, 'bottomLeft')}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <Button
                                            icon={record.status ? <LockOutlined /> : <UnlockOutlined />}
                                            size='small'
                                        />
                                    </Popconfirm>
                                </Tooltip>
                            )
                        }
                        {/* delete icon */}
                        {btnPermission.delete && (
                            <Tooltip title="Delete Department" placement="top">
                                <Popconfirm
                                    title="Do you want to delete this department?"
                                    onConfirm={() => deleteHandler(record)}
                                    onCancel={() => openNotificationWithIcon('error', 'Abort', `Delete cancelled`, 'bottomLeft')}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        danger
                                        type="primary"
                                        icon={<DeleteOutlined />}
                                        size='small'
                                        disabled={record.status}
                                    />
                                </Popconfirm>
                            </Tooltip>
                        )}
                    </Flex>
                </Flex>
            ),
            //   ...getColumnSearchProps('action'), // You may need to define getColumnSearchProps function
        },
    ];

    const applySearchAndFilters = (data, searchText, filters) => {
        return data.filter((department) => {
            const matchesSearch = searchText ? 
                (department.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
                (department.code || '').toLowerCase().includes(searchText.toLowerCase())
                : true;

            const matchesFilters = filters.length > 0 ?
                filters.every(({ key, value }) => {
                    if (!value) return false;
                    const itemValue = (department[key]?.toString() || '').toLowerCase();
                    return itemValue.startsWith(value.toLowerCase());
                })
                : true;

            return matchesSearch && matchesFilters;
        })
    }

    const editHandler = (records) => {
        setDepartmentId(records.id);
        setInitialDepartmentData({
            code: records.code,
            name: records.name,
        });
        form.setFieldsValue({
            code: records.code,
            name: records.name,
        });
        setActionState("edit");
        setOpen(true);
    }

    const editStatusHandler = async (records) => {
        const formData = {
            status: !records.status,
        }
        await MasterDataAPI.updateStatusDepartment(records.id, formData, companyId, token).then((res) => {
            openNotificationWithIcon('success', 'Successful', `${res.message}`, 'bottomLeft');
            const updatedDepartmet = res.data?.[0]
            if (!updatedDepartmet) return;

            const updatedSourceList = departmentData.map(department =>
                department.id === updatedDepartmet.id ? updatedDepartmet : department
            )
            setDepartmentData(updatedSourceList)

            const matchesSearch = (department) => {
                return searchInputValue
                    ? department.name.toLowerCase().includes(searchInputValue.toLowerCase()) ||
                    department.code.toLowerCase().includes(searchInputValue.toLowerCase())
                    : true;
            };

            const matchesFilter = (department) => {
                return filterValues.length > 0
                    ? filterValues.every(({ key, value }) =>
                        department[key]?.toString().toLowerCase().startsWith(value.toLowerCase())
                    )
                    : true;
            };

            const filtered = updatedSourceList.filter(department =>
                matchesSearch(department) && matchesFilter(department)
            );

            setTempDepartmentData(filtered);
        }).catch((err) => {
            if (err.response) {
                openNotificationWithIcon('error', 'Department status change failed', `${err.response.data.message}`, 'bottomLeft');
            } else {
                openNotificationWithIcon('error', 'Department status change failed', `${err.message}`, 'bottomLeft');
            }
        })
    }

    const deleteHandler = async (records) => {
        await MasterDataAPI.deleteDepartment(records.id, companyId, token).then((res) => {
            openNotificationWithIcon('success', 'Successful', `${res.message}`, 'bottomLeft');

            const updatedSourceList = departmentData.filter(department => department.id !== records.id);
            setDepartmentData(updatedSourceList);
            const matchesSearch = (department) => {
                return searchInputValue
                    ? department.name.toLowerCase().includes(searchInputValue.toLowerCase()) ||
                    department.code.toLowerCase().includes(searchInputValue.toLowerCase())
                    : true;
            };

            const matchesFilter = (department) => {
                return filterValues.length > 0
                    ? filterValues.every(({ key, value }) =>
                        department[key]?.toString().toLowerCase().startsWith(value.toLowerCase())
                    )
                    : true;
            };

            const filtered = updatedSourceList.filter(department =>
                matchesSearch(department) && matchesFilter(department)
            );

            setTempDepartmentData(filtered);
        }).catch((err) => {
            if (err.response) {
                openNotificationWithIcon('error', 'Department deletion failed', `${err.response.data.message}`, 'bottomLeft');
            } else {
                openNotificationWithIcon('error', 'Department deletion failed', `${err.message}`, 'bottomLeft');
            }
        })
    }

    const validateBeforeSubmit = async () => {
        try {
            await form.validateFields();
            return true;
        } catch (errorInfo) {
            return false;
        }
    };

    const handleSubmit = async () => {
        const validation = await validateBeforeSubmit();
        if (!validation) {
            return;
        }

        setCreateButtonLoading(true);
        const formValues = form.getFieldsValue();
        const trimmedPayload = {
            code: formValues.code.trim(),
            name: formValues.name.trim(),
        };

        if (actionState === "add") {
            try {
                const { message, data } = await MasterDataAPI.createDepartment(trimmedPayload, companyId, token);
                openNotificationWithIcon('success', 'Successful', `${message}`, 'bottomLeft');
                const updatedSourceList = [...departmentData, data];
                setDepartmentData(updatedSourceList);
                const updatedTempList = applySearchAndFilters(updatedSourceList, searchInputValue, filterValues);
                setTempDepartmentData(updatedTempList);
                onClose();
            } catch (error) {
                if (error.response) {
                    openNotificationWithIcon('error', 'Department creation failed', `${error.response.data.message}`, 'bottomLeft');
                } else {
                    openNotificationWithIcon('error', 'Department creation failed', `${error.message}`, 'bottomLeft');
                }
            } finally {
                setShowPopconfirm(false);
                setCreateButtonLoading(false);
            }
        }

        if (actionState === "edit") {
            try {
                const { message, data } = await MasterDataAPI.updateDepartment(departmentId, trimmedPayload, companyId, token);
                openNotificationWithIcon('success', 'Successful', `${message}`, 'bottomLeft');
                const updatedSourceList = departmentData.map(department =>
                    department.id === departmentId ? data : department
                );
                setDepartmentData(updatedSourceList);
                const updatedTempList = applySearchAndFilters(updatedSourceList, searchInputValue, filterValues);
                setTempDepartmentData(updatedTempList);
                onClose();
            } catch (error) {
                if (error.response) {
                    openNotificationWithIcon('error', 'Department update failed', `${error.response.data.message}`, 'bottomLeft');
                } else {
                    openNotificationWithIcon('error', 'Department update failed', `${error.message}`, 'bottomLeft');
                }
            } finally {
                setShowPopconfirm(false);
                setCreateButtonLoading(false);
            }
        }
    }

    
    const filterDataByCriteria = (data, criteria) => {
        return data.filter(item => {
            return criteria.every(({ key, value }) => {
                // to-do put for status also
                if (!item[key] || !value) return false;
                const itemValue = (item[key]?.toString() || '').toLowerCase();
                const lowerCasePrefix = (value || '').toLowerCase();
                return itemValue.includes(lowerCasePrefix);
            });
        });
    };

    // used for search filter 
    const handleSearchFunction = (value) => {
        setSearchInputValue(value);
        if (value) {
            if (filterValues.length > 0) {
                const filteredData = tempDepartmentData.filter(item =>
                    (item.name || '').toLowerCase().includes(value.toLowerCase()) ||
                    (item.code || '').toLowerCase().includes(value.toLowerCase())
                );
                setTempDepartmentData(filteredData);
            }
            else {
                const filteredData = departmentData.filter(item =>
                    (item.name || '').toLowerCase().includes(value.toLowerCase()) ||
                    (item.code || '').toLowerCase().includes(value.toLowerCase())
                );
                setTempDepartmentData(filteredData);
            }
        } else {
            if (filterValues.length > 0) {
                const filterData = filterDataByCriteria(departmentData, filterValues);
                setTempDepartmentData(filterData);
            } else {
                setTempDepartmentData(departmentData);
            }
        }
    };

    const handleFilterSearchFunction = (value) => {
        if (value) {
            if (searchInputValue?.length > 0) {
                const filteredData = departmentData.filter(item =>
                    (item.name || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
                    (item.code || '').toLowerCase().includes(searchInputValue.toLowerCase())
                );
                const filterData2 = filterDataByCriteria(filteredData, value);
                setTempDepartmentData(filterData2);

            } else {
                const filterData = filterDataByCriteria(departmentData, value);
                setTempDepartmentData(filterData);
            }
        } else {
            if (searchInputValue?.length > 0) {
                const filteredData = departmentData.filter(item =>
                    (item.name || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
                    (item.code || '').toLowerCase().includes(searchInputValue.toLowerCase())
                );
                setTempDepartmentData(filteredData);
            } else {
                setTempDepartmentData(departmentData);
            }
        }
    }


    const handleFilterDetailsChange = (value) => {
        setFilterValues(value)
    }

    const handleRemoveTag = (key) => {
        setFilterValues(prev =>
            prev.filter(item => item.key !== key)
        );
    }

    useEffect(() => {
        setIsDepartmentListLoading(true);
        getDepartmentsData();
    }, [companyId]);

    useEffect(() => {
        handleFilterSearchFunction(filterValues);
        setCurrentPage(1); // Reset to page 1 when filters change
    }, [filterValues])

    useEffect(() => {
        handleSearchFunction(searchInputValue)
        setCurrentPage(1); // Reset to page 1 when search changes
    }, [searchInputValue])

    const handleCreateUpdateClick = async (e) => {
        e.preventDefault();
        let validation = await validateBeforeSubmit();

        if (actionState === "add" && validation) {
            handleSubmit();
        }
        else {
            if (validation) {
                setShowPopconfirm(true)
            } else {
                setShowPopconfirm(false)
            }
        }
    }

    const handleCancel = () => {
        setCancelButtonDisabled(true);
        openNotificationWithIcon('error', 'Abort', `${actionState === "add" ? "Create" : "Update"} cancelled`, 'bottomLeft')
        setTimeout(() => {
            setShowPopconfirm(false);
            setCancelButtonDisabled(false);
        }, 300);
    };

    useEffect(() => {
        setBtnPermission({
            "add": false,
            "edit": false,
            "delete": false,
            "view": false,
            "activeInactive": false
        })
        if (role.length > 0) {
            role.forEach(value => {
                if (value.restricted) {
                    if (value.roleName === "Company Admin") {
                        setBtnPermission({
                            "add": true,
                            "edit": true,
                            "delete": true,
                            "view": true,
                            "activeInactive": true
                        })
                    } else if (value.roleName === "Admin") {
                        setBtnPermission({
                            "add": true,
                            "edit": true,
                            "delete": true,
                            "view": true,
                            "activeInactive": true
                        })
                    }
                } else if (value?.permissions) {
                    console.log('value?.permissions :', value?.permissions)

                    const permissionData = value.permissions.find(data => data.permissionName === "Department");

                    if (permissionData) {
                        permissionData.permissionLevel.map(value => {
                            switch (value) {
                                case "create":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        add: true
                                    }));
                                    break;
                                case "view":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        view: true
                                    }));
                                    break;
                                case "edit":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        edit: true
                                    }));
                                    break;
                                case "delete":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        delete: true
                                    }));
                                    break;
                                case "Active / Inactive":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        activeInactive: true
                                    }));
                                    break;
                            }
                        })
                    } else {
                        setBtnPermission({
                            "add": false,
                            "edit": false,
                            "delete": false,
                            "view": false,
                            "activeInactive": false
                        })
                    }
                }
            });
        }
    }, []);

    const filterTags = filterValues.filter(filterObject => filterObject.value.trim() !== "");

    return (
        <>
            <Flex gap={20} justify='space-between' align='center' style={{
                margin: '16px',
                padding: '16px 24px',
                // minHeight: 280,
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
            }}>
                <Breadcrumb
                    items={[
                        {
                            // href: '',
                            title: <HomeOutlined />,
                        },
                        {
                            title: 'Master Data / Department',
                        },
                    ]}
                />
                <Flex justify='center' align='center' gap={12}>
                    {btnPermission.view && (
                        <>
                            <Button onClick={showFilterDrawer} icon={<FilterOutlined style={{ paddingLeft: 8, paddingRight: 8 }} />} />
                            <Search
                                placeholder="Search"
                                value={searchInputValue}
                                onChange={(e) => setSearchInputValue(e.target.value)}
                            />

                        </>
                    )}

                    {btnPermission.add && (
                        <Button
                            type="primary"
                            onClick={showDrawer}
                        >
                            Add New
                        </Button>
                    )}
                </Flex>
            </Flex>
            <div style={{ margin: '16px' }}>
                {
                    filterTags.length !== 0 &&
                    <Flex align='center' gap={12}>
                        {filterTags.map(item => (
                            <Tag bordered={false} closable color="success" key={item?.key} onClose={() => handleRemoveTag(item?.key)}>
                                {item?.value}
                            </Tag>
                        ))}
                    </Flex>
                }
            </div>
            <div style={{
                margin: '16px',
                padding: 24,
                minHeight: "78svh",
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
            }}>
                {isDepartmentListLoading ? (<>
                    <CommonSkeletonLoader />
                </>) : (<>
                    <Table
                        columns={columns}
                        dataSource={(tempDepartmentData && tempDepartmentData.length > 0 ? tempDepartmentData : [])}
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            current: currentPage,
                            pageSize,
                            onChange: (page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            },
                            onShowSizeChange: (current, size) => {
                                setCurrentPage(1);
                                setPageSize(size);
                            },
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            selectProps: {
                                showSearch: false,
                                inputReadOnly: true,
                                onInputKeyDown: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                },
                                onKeyDown: (e) => {
                                    // Allow only arrow keys, Enter, Escape, Tab
                                    if (!['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab'].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }
                            }
                        }}
                    />
                </>)}
                <Drawer title={actionState === 'add' ? "Add Department" : "Update Department"}
                    onClose={onClose}
                    open={open}
                    footer={[
                        <Flex
                            justify='flex-end'
                            align='center'
                            gap='large'
                            key="footer-buttons"
                        >
                            <Button onClick={onClose}>
                                Cancel
                            </Button>

                            {
                                actionState === 'add' ? (
                                    <Button type="primary" onClick={handleCreateUpdateClick} loading={createButtonLoading} disabled={createButtonLoading}>
                                        Create
                                    </Button>) : (<Popconfirm
                                        title={`Do you want to update the department?`}
                                        open={showPopconfirm}
                                        onConfirm={() => handleSubmit()}
                                        onCancel={handleCancel}
                                        okText="Yes"
                                        cancelText="No"
                                        cancelButtonProps={{ disabled: cancelButtonDisabled }}
                                    >
                                        <Button 
                                            type="primary" 
                                            onClick={handleCreateUpdateClick}
                                            disabled={!isFormModified()}
                                        >
                                            Update
                                        </Button>
                                    </Popconfirm>)
                            }
                        </Flex>
                    ]
                    }
                >
                    <Form 
                        form={form}
                        layout="vertical"
                        onValuesChange={(changedValues, allValues) => {
                            if(actionState === 'add')return;
                        }}
                    >
                        <Form.Item 
                            label="Department Code"
                            name="code"
                            rules={getDepartmentCodeRules()}
                        >
                            <Input 
                                placeholder="Code"
                                autoComplete="new-code" 
                            />
                        </Form.Item>
                        <Form.Item 
                            label="Department Name"
                            name="name"
                            rules={getDepartmentNameRules()}
                        >
                            <Input
                                placeholder="Name"
                                autoComplete="new-name" 
                            />
                        </Form.Item>
                    </Form>
                </Drawer>
                <FilterComponent
                    openDrawer={openFilter}
                    closeDrawer={onCloseFilter}
                    data={tempDepartmentData}
                    handleFilterDetailsChange={handleFilterDetailsChange}
                    filterValues={filterValues}
                    screenName="masterData_department"
                    resetFilterValues={() => setFilterValues([])}
                />
                {contextHolder}
            </div>
        </>
    )
}